import type { NotificationItem } from '@/types/shared';

/**
 * Merges a batch of notifications into the panel's existing list, de-duplicated by `id` (T115,
 * per the architecture doc's "dedup of a live-push-vs-backfill race is by `notification.id` on the
 * client" and the UX handoff's "merged/deduped ... the user-visible outcome is simply the badge is
 * always correct, panel never shows a duplicate row").
 *
 * `incoming` entries win over `existing` ones with the same id (they are always the fresher server
 * state — e.g. a backfill/mark-read response reconciling a row's `readAt`). The result is re-sorted
 * newest-first (`createdAt` desc) — the design's one fixed ordering rule (16-notifications-ux.md
 * §2.5, "newest-first, always") — since a backfill or a single live arrival is not guaranteed to
 * already be in the right position relative to whatever the panel already holds.
 */
export function mergeNotifications(
  existing: NotificationItem[],
  incoming: NotificationItem[],
): NotificationItem[] {
  if (incoming.length === 0) return existing;

  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    byId.set(item.id, item);
  }

  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}
